import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { agreementApi } from '../utils/api';
import { formatCurrencyForDisplay, formatDateForDisplay } from '@kist/shared';
import { 
  ArrowRight, 
  Download, 
  Share2, 
  FileText, 
  CreditCard, 
  Calendar,
  User,
  Building,
  CheckCircle,
  AlertCircle,
  Clock,
  MessageCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

interface AgreementSummary {
  id: string;
  short_code: string;
  item_desc: string;
  serial?: string;
  total_price: number;
  deposit: number;
  admin_fee_total: number;
  schedule_type: 'weekly' | 'bi-weekly' | 'monthly';
  installment_count: number;
  due_day: number;
  grace_days: number;
  start_date: string;
  end_date: string;
  remaining_balance: number;
  installment_amount: number;
  status: 'active' | 'completed' | 'overdue';
  signature_url?: string;
  created_at: string;
  updated_at: string;
  installments: Array<{
    seq: number;
    due_date: string;
    amount_due: number;
    status: 'pending' | 'paid' | 'overdue';
    paid_date?: string;
  }>;
}

export const AgreementSummaryPage: React.FC = () => {
  const { shortCode } = useParams<{ shortCode: string }>();
  const [agreement, setAgreement] = useState<AgreementSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (shortCode) {
      loadAgreement(shortCode);
    }
  }, [shortCode]);

  const loadAgreement = async (code: string) => {
    try {
      setLoading(true);
      const data = await agreementApi.getByCode(code);
      setAgreement(data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load agreement';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!agreement) return;
    
    try {
      toast.loading('Generating PDF...');
      // In a real implementation, this would call the PDF generation endpoint
      // const pdfUrl = await agreementApi.generatePDF(agreement.id);
      // window.open(pdfUrl, '_blank');
      toast.success('PDF download started');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const handleShare = async () => {
    if (!agreement) return;
    
    const shareUrl = `${window.location.origin}/a/${agreement.short_code}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `اتفاقية البيع بالتقسيط - ${agreement.item_desc}`,
          text: `عرض اتفاقية البيع بالتقسيط: ${agreement.item_desc}`,
          url: shareUrl,
        });
      } catch (error) {
        // User cancelled sharing
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('تم نسخ الرابط إلى الحافظة');
      } catch (error) {
        toast.error('فشل في نسخ الرابط');
      }
    }
  };

  const handleWhatsAppShare = () => {
    if (!agreement) return;
    
    const shareUrl = `${window.location.origin}/a/${agreement.short_code}`;
    
    // Create WhatsApp share URL with prefilled Arabic text
    const whatsappText = `مرحباً! 👋

📋 *اتفاقية البيع بالتقسيط*
${agreement.item_desc}

💰 *التفاصيل المالية:*
• السعر الإجمالي: ${formatCurrencyForDisplay(agreement.total_price)}
• الدفعة المقدمة: ${formatCurrencyForDisplay(agreement.deposit)}
• المبلغ المتبقي: ${formatCurrencyForDisplay(agreement.remaining_balance)}
• رسوم الإدارة: ${formatCurrencyForDisplay(agreement.admin_fee_total)}

📅 *جدول الدفع:*
• عدد الأقساط: ${agreement.installment_count}
• نوع الجدولة: ${getScheduleTypeArabic(agreement.schedule_type)}
• مبلغ القسط: ${formatCurrencyForDisplay(agreement.installment_amount)}
• يوم الاستحقاق: ${agreement.due_day}
• أيام السماح: ${agreement.grace_days}

🔗 *عرض التفاصيل الكاملة:*
${shareUrl}

---
تم إرسال هذا الرابط من نظام إدارة الاتفاقيات
رمز الاتفاقية: ${agreement.short_code}`;

    // Encode the text for WhatsApp
    const encodedText = encodeURIComponent(whatsappText);
    const whatsappUrl = `https://wa.me/?text=${encodedText}`;
    
    // Open WhatsApp in new window/tab
    window.open(whatsappUrl, '_blank');
  };

  const handleWhatsAppShareCompact = () => {
    if (!agreement) return;
    
    const shareUrl = `${window.location.origin}/a/${agreement.short_code}`;
    
    // Create compact WhatsApp share URL
    const compactText = `📋 اتفاقية البيع بالتقسيط: ${agreement.item_desc}

💰 ${formatCurrencyForDisplay(agreement.total_price)} | ${agreement.installment_count} قسط
📅 ${getScheduleTypeArabic(agreement.schedule_type)}

🔗 ${shareUrl}

رمز الاتفاقية: ${agreement.short_code}`;

    // Encode the text for WhatsApp
    const encodedText = encodeURIComponent(compactText);
    const whatsappUrl = `https://wa.me/?text=${encodedText}`;
    
    // Open WhatsApp in new window/tab
    window.open(whatsappUrl, '_blank');
  };

  const getScheduleTypeArabic = (scheduleType: string) => {
    switch (scheduleType) {
      case 'weekly': return 'أسبوعي';
      case 'bi-weekly': return 'كل أسبوعين';
      case 'monthly': return 'شهري';
      default: return scheduleType;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'completed':
        return 'text-blue-600 bg-blue-100';
      case 'overdue':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'overdue':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getInstallmentStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'overdue':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">جاري تحميل الاتفاقية...</p>
        </div>
      </div>
    );
  }

  if (error || !agreement) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">خطأ في تحميل الاتفاقية</h1>
          <p className="text-gray-600 mb-6">{error || 'الاتفاقية غير موجودة'}</p>
          <Link
            to="/"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowRight className="w-5 h-5 ml-2" />
            العودة للصفحة الرئيسية
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" dir="rtl">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                ملخص الاتفاقية - Agreement Summary
              </h1>
              <p className="text-gray-600">
                رمز الاتفاقية: <span className="font-mono font-semibold">{agreement.short_code}</span>
              </p>
            </div>
            <div className="flex items-center space-x-4 space-x-reverse">
              <button
                onClick={handleDownloadPDF}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="w-4 h-4 ml-2" />
                تحميل PDF
              </button>
              <button
                onClick={handleShare}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Share2 className="w-4 h-4 ml-2" />
                مشاركة
              </button>
              <button
                onClick={handleWhatsAppShare}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <MessageCircle className="w-4 h-4 ml-2" />
                مشاركة على واتساب
              </button>
              <button
                onClick={handleWhatsAppShareCompact}
                className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                title="مشاركة مختصرة على واتساب"
              >
                <MessageCircle className="w-4 h-4 ml-2" />
                مشاركة مختصرة
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Agreement Status */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">حالة الاتفاقية</h2>
                <div className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(agreement.status)}`}>
                  {getStatusIcon(agreement.status)}
                  <span className="mr-2">
                    {agreement.status === 'active' && 'نشطة'}
                    {agreement.status === 'completed' && 'مكتملة'}
                    {agreement.status === 'overdue' && 'متأخرة'}
                  </span>
                </div>
              </div>
            </div>

            {/* Item Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center mb-4">
                <FileText className="w-5 h-5 text-blue-600 ml-2" />
                <h2 className="text-lg font-semibold text-gray-900">معلومات السلعة</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">وصف السلعة</label>
                  <p className="text-gray-900 font-medium">{agreement.item_desc}</p>
                </div>
                {agreement.serial && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">الرقم التسلسلي</label>
                    <p className="text-gray-900 font-mono">{agreement.serial}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Financial Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center mb-4">
                <CreditCard className="w-5 h-5 text-green-600 ml-2" />
                <h2 className="text-lg font-semibold text-gray-900">المعلومات المالية</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">السعر الإجمالي:</span>
                    <span className="font-semibold">{formatCurrencyForDisplay(agreement.total_price)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">الدفعة المقدمة:</span>
                    <span className="font-semibold">{formatCurrencyForDisplay(agreement.deposit)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">رسوم الإدارة:</span>
                    <span className="font-semibold">{formatCurrencyForDisplay(agreement.admin_fee_total)}</span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">المبلغ المتبقي:</span>
                    <span className="font-semibold text-green-600">{formatCurrencyForDisplay(agreement.remaining_balance)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">مبلغ القسط:</span>
                    <span className="font-semibold text-blue-600">{formatCurrencyForDisplay(agreement.installment_amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">عدد الأقساط:</span>
                    <span className="font-semibold">{agreement.installment_count}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Schedule */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center mb-4">
                <Calendar className="w-5 h-5 text-purple-600 ml-2" />
                <h2 className="text-lg font-semibold text-gray-900">جدول الدفع</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نوع الجدول</label>
                  <p className="text-gray-900">
                    {agreement.schedule_type === 'weekly' && 'أسبوعي'}
                    {agreement.schedule_type === 'bi-weekly' && 'كل أسبوعين'}
                    {agreement.schedule_type === 'monthly' && 'شهري'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">يوم الاستحقاق</label>
                  <p className="text-gray-900">اليوم {agreement.due_day} من كل {agreement.schedule_type === 'weekly' ? 'أسبوع' : agreement.schedule_type === 'bi-weekly' ? 'أسبوعين' : 'شهر'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاريخ البداية</label>
                  <p className="text-gray-900">{formatDateForDisplay(new Date(agreement.start_date))}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاريخ الانتهاء</label>
                  <p className="text-gray-900">{formatDateForDisplay(new Date(agreement.end_date))}</p>
                </div>
              </div>

              {/* Installments List */}
              <div className="max-h-96 overflow-y-auto">
                <h3 className="font-semibold text-gray-900 mb-3">الأقساط</h3>
                <div className="space-y-2">
                  {agreement.installments.map((installment) => (
                    <div
                      key={installment.seq}
                      className={`p-3 rounded-lg border ${getInstallmentStatusColor(installment.status)}`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">القسط {installment.seq}</span>
                        <span className="text-sm font-semibold">{formatCurrencyForDisplay(installment.amount_due)}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatDateForDisplay(new Date(installment.due_date))}
                        {installment.paid_date && (
                          <span className="mr-2">• تم الدفع: {formatDateForDisplay(new Date(installment.paid_date))}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Signature */}
            {agreement.signature_url && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center mb-4">
                  <User className="w-5 h-5 text-indigo-600 ml-2" />
                  <h2 className="text-lg font-semibold text-gray-900">التوقيع</h2>
                </div>
                
                <div className="text-center">
                  <img
                    src={agreement.signature_url}
                    alt="توقيع العميل"
                    className="max-w-xs mx-auto border rounded-lg"
                  />
                  <p className="text-sm text-gray-600 mt-2">توقيع العميل</p>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-6">
              <div className="flex items-center mb-4">
                <Building className="w-5 h-5 text-orange-600 ml-2" />
                <h2 className="text-lg font-semibold text-gray-900">معلومات إضافية</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاريخ الإنشاء</label>
                  <p className="text-gray-900">{formatDateForDisplay(new Date(agreement.created_at))}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">آخر تحديث</label>
                  <p className="text-gray-900">{formatDateForDisplay(new Date(agreement.updated_at))}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">أيام السماح</label>
                  <p className="text-gray-900">{agreement.grace_days} يوم</p>
                </div>

                <div className="pt-4 border-t">
                  <h3 className="font-semibold text-gray-900 mb-2">إحصائيات سريعة</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>الأقساط المدفوعة:</span>
                      <span className="font-semibold text-green-600">
                        {agreement.installments.filter(i => i.status === 'paid').length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>الأقساط المتأخرة:</span>
                      <span className="font-semibold text-red-600">
                        {agreement.installments.filter(i => i.status === 'overdue').length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>الأقساط المعلقة:</span>
                      <span className="font-semibold text-gray-600">
                        {agreement.installments.filter(i => i.status === 'pending').length}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 